/**
 * Icon Registry - Maps icon names to CDN URLs
 * 
 * Provides centralized icon management with CDN hosting.
 * Icons should be 16x16 SVGs for use with Cloudscape Icon component.
 * 
 * Usage:
 * - Add new icons by adding entries to the registry
 * - Icons are loaded from CDN at runtime
 * - Use with Cloudscape Icon component: <Icon url={getIconUrl('cart')} />
 */

// CDN base path for icons
const ICON_CDN_BASE = 'https://d3216uwaav9lg7.cloudfront.net/icons';

// Media Central hosted assets
export const MEDIA_CENTRAL_ICONS = {
  playButton: 'https://m.media-amazon.com/images/G/01/search-browse/Polyphemus/play_button_small.png',
} as const;

/**
 * Icon registry mapping icon names to CDN paths
 * 
 * All icons should be 16x16 SVGs optimized for web
 */
export const ICON_REGISTRY = {
  // Commerce icons
  'cart': `${ICON_CDN_BASE}/cart.svg`,
  'storefront': `${ICON_CDN_BASE}/storefront.svg`,
  'package': `${ICON_CDN_BASE}/package.svg`,
  
  // Analytics icons
  'analytics': `${ICON_CDN_BASE}/analytics.svg`,
  'star': `${ICON_CDN_BASE}/star.svg`,
  
  // UI icons
  'check': `${ICON_CDN_BASE}/check.svg`,
  
  // Add more icons as needed
} as const;

export type IconName = keyof typeof ICON_REGISTRY;

/**
 * Get CDN URL for an icon by name
 * 
 * @param name - Icon name from registry
 * @returns CDN URL for the icon, or undefined if not found
 */
export function getIconUrl(name: IconName): string | undefined {
  return ICON_REGISTRY[name];
}

/**
 * Check if an icon exists in the registry
 * 
 * @param name - Icon name to check
 * @returns true if icon exists in registry
 */
export function hasIcon(name: string): name is IconName {
  return name in ICON_REGISTRY;
}

/**
 * Get all available icon names
 * 
 * @returns Array of all icon names in registry
 */
export function getAvailableIcons(): IconName[] {
  return Object.keys(ICON_REGISTRY) as IconName[];
}
